import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Scale } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="space-y-6" data-testid="terms-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <Scale className="h-7 w-7 text-amber-400" />
          Terms of Service
        </h1>
        <p className="text-slate-400 mt-1 text-sm">The rules of using Seafarer Logbook</p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">1. About These Terms</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>These Terms of Service (&quot;Terms&quot;) form a binding agreement between you (&quot;you&quot;, &quot;the user&quot;) and Seafarer Logbook (&quot;we&quot;, &quot;us&quot;, &quot;the service&quot;) for the use of the Seafarer Logbook web application at <a href="https://www.seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">www.seafarerlogbook.co.uk</a> and any related services (collectively, the &quot;Service&quot;).</p>
          <p>By registering for or using the Service, you confirm that you have read, understood, and agreed to be bound by these Terms. If you do not agree, please do not use the Service.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">2. The Service</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Seafarer Logbook is a software tool that helps UK seafarers track their qualifying days under HMRC&rsquo;s Seafarer&rsquo;s Earnings Deduction (SED) rules (HS205), maintain a log of trips and earnings, store wage-slip records, and produce reports for personal record-keeping and submission to accountants or HMRC.</p>
          <p><strong className="text-slate-300">The Service does not constitute tax, legal, or financial advice.</strong> All outputs — including SED eligibility indications, day counts, earnings totals, and tax estimates — are provided for informational purposes only and must be reviewed by you and your accountant before being relied upon. You remain solely responsible for the accuracy of your tax return.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">3. Eligibility &amp; Account Registration</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>You must be at least 18 years old and legally able to enter into a contract to register an account.</p>
          <p>You are responsible for keeping your login credentials confidential. You must notify us at <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a> immediately if you suspect unauthorised access.</p>
          <p>You agree to provide accurate, up-to-date information when registering and to update it if it changes. Accounts opened with false information may be suspended or terminated.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">4. Subscription &amp; Payment</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>The Service is offered on a free tier and on paid subscription tiers (e.g. Monthly £3.99 and Yearly £45, prices may change with notice). Current pricing is shown in the app at the time of subscription.</p>
          <p>Payments are processed by <strong className="text-slate-300">Stripe</strong>. We do not store your full card details — only transaction references and renewal status. By subscribing, you authorise Stripe to charge your chosen payment method automatically at each renewal date until you cancel.</p>
          <p>You can cancel your subscription at any time from the Settings page or via the Stripe customer portal. Cancellation takes effect at the end of the current billing period — your access continues until then.</p>
          <p>If a payment fails, we may suspend access until the issue is resolved. Repeated failures may result in subscription cancellation.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">5. Your Data &amp; Privacy</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Your use of the Service is also governed by our <a href="/privacy-public" className="text-amber-400 hover:text-amber-300">Privacy Policy</a>, which explains what personal data we collect, why, and your rights under UK GDPR.</p>
          <p>You retain ownership of all trip, earnings, and document data you enter into the Service. You grant us a limited licence to process that data solely to provide the Service to you.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">6. Acceptable Use</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>You agree NOT to:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Use the Service for any unlawful or fraudulent purpose, including submitting false data to obtain tax relief you are not entitled to</li>
            <li>Upload documents that contain illegal content, malware, or material you do not have the right to upload</li>
            <li>Attempt to reverse-engineer, scrape, copy, or resell the Service or any part of it</li>
            <li>Share your account with other people; each subscription is for one named individual</li>
            <li>Use the Service to harass, abuse, or defame any other person — including via the in-app feedback or messaging features</li>
            <li>Interfere with or attempt to compromise the security or availability of the Service for other users</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that breach these rules, with or without notice depending on severity.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">7. Referrals &amp; Invitations</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>You may invite friends or colleagues to join the Service using the in-app invite feature. Successful referrals may earn you additional free subscription days as described in the app.</p>
          <p>Invitations expire after 24 hours. Referral rewards are credited only after the invited person registers and remains active for a minimum period. We reserve the right to revoke rewards earned by abuse of the referral system (e.g. self-invites, fake accounts).</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">8. Availability &amp; Changes</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>We aim to keep the Service available but do not guarantee uninterrupted access. Planned maintenance, third-party outages, or unexpected technical issues may cause short interruptions.</p>
          <p>We may update, change, or remove features at any time. Material changes that significantly reduce paid functionality will be communicated in advance via email or in-app notice.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">9. Limitation of Liability</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>To the maximum extent permitted by law, Seafarer Logbook is not liable for:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Any loss arising from reliance on data, calculations, or reports produced by the Service without independent review by a qualified tax adviser</li>
            <li>Any indirect, consequential, or special loss (e.g. lost profits, missed deadlines, penalties imposed by HMRC)</li>
            <li>Data loss caused by your own actions (e.g. accidental deletion) or by third-party providers (e.g. Stripe, the hosting platform)</li>
          </ul>
          <p>Our total liability to you in any 12-month period is limited to the amount you have paid us for the Service in that period.</p>
          <p>Nothing in these Terms excludes or limits liability that cannot be excluded under UK law, including for death, personal injury caused by negligence, or fraud.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">10. Refunds &amp; Cancellation</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Refunds are governed by our separate <a href="/refund-public" className="text-amber-400 hover:text-amber-300">Refund Policy</a>, which sets out your rights under UK consumer law and our discretionary refund stance.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">11. Termination</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>You may close your account at any time from the Settings page. We may suspend or terminate your account if you breach these Terms, fail to pay, or use the Service in a way that harms us or other users.</p>
          <p>On termination, your access ceases. We may retain limited records (e.g. financial transactions) for as long as required by law, but will delete your personal data in accordance with our Privacy Policy.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">12. Changes to These Terms</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>We may update these Terms from time to time. The &quot;last updated&quot; date below will reflect the most recent revision. Material changes will be communicated by email or in-app notice. Continued use of the Service after a change constitutes acceptance of the new Terms.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">13. Governing Law</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales, except where mandatory consumer-protection law gives you a right to bring proceedings in your country of residence.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">14. Contact</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>For any questions about these Terms, email <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a>.</p>
          <p className="text-xs text-slate-500 mt-3">Last updated: April 2026.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsOfService;
